/**
 * ConsentStage — the mandatory always-on Stage 0 of every intake.
 *
 * Renders the owner-approved consent & privacy copy VERBATIM (from
 * `lib/consentContent.ts`) and captures an explicit acceptance: every REQUIRED
 * tick-box must be ticked before the flow can advance. The optional astro/
 * health box rides along but does not gate.
 *
 * Pure-white, semantic-token UI consistent with the locked ONE-UI system. The
 * §7 co-brand passage is rendered only when `partnerEngagement` (OQ-3 — the
 * co-brand fact comes from the Partner record, not an intake field).
 */
import { Box, Btn3 } from './ui';
import {
  CONSENT_INTRO,
  CONSENT_SECTIONS,
  CONSENT_CHECKS,
  CONSENT_CONFIRM_HEADING,
} from '../lib/consentContent';

const para: React.CSSProperties = { margin: '6px 0', fontSize: 13, lineHeight: 1.55 };
const h3: React.CSSProperties = {
  fontSize: 13.5,
  fontWeight: 700,
  color: 'var(--accent, #2f5d8a)',
  margin: '16px 0 4px',
};

export function ConsentStage({
  flags,
  onToggle,
  onAccept,
  saving,
  alreadyAccepted,
  readOnly,
  partnerEngagement = false,
  hideAccept = false,
}: {
  /** Current tick-box state, keyed by ConsentCheckItem.key. */
  flags: Record<string, boolean>;
  onToggle: (key: string, value: boolean) => void;
  /** Called when the client confirms (all required boxes ticked). */
  onAccept: () => void;
  saving?: boolean;
  /** True once a satisfying acceptance is on the doc. */
  alreadyAccepted?: boolean;
  /** When read-only (already accepted, viewing back), boxes are disabled. */
  readOnly?: boolean;
  /** Show §7 only in a channel-partner engagement (OQ-3). */
  partnerEngagement?: boolean;
  /**
   * Hide the in-component "I agree — continue" button. Used on the final
   * "Review & submit" tab (owner ruling 2026-06-22), where the page's own
   * Save & Submit button captures the acceptance and submits in one action —
   * the boxes here only drive the tick-state that gates that button.
   */
  hideAccept?: boolean;
}) {
  const requiredOk = CONSENT_CHECKS.filter((c) => c.required).every(
    (c) => flags[c.key] === true,
  );

  return (
    <>
      <Box title="Consent & privacy" note="Required to continue">
        <h3 style={{ ...h3, marginTop: 0, color: 'var(--ink)' }}>
          {CONSENT_INTRO.heading}
        </h3>
        <p style={para}>{CONSENT_INTRO.para}</p>

        {CONSENT_SECTIONS.filter(
          (s) => !s.partnerOnly || partnerEngagement,
        ).map((s) => (
          <div key={s.heading}>
            <h3 style={h3}>{s.heading}</h3>
            {(s.paras || []).map((p, i) => (
              <p key={i} style={para}>
                {p}
              </p>
            ))}
            {s.bullets && s.bullets.length > 0 && (
              <ul style={{ margin: '4px 0 8px', paddingLeft: 20 }}>
                {s.bullets.map((b, i) => (
                  <li key={i} style={{ fontSize: 13, lineHeight: 1.5, margin: '3px 0' }}>
                    {b}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </Box>

      <Box title={CONSENT_CONFIRM_HEADING} note="Tick each box to continue">
        {CONSENT_CHECKS.map((c) => (
          <label
            key={c.key}
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
              margin: '0 0 12px',
              fontSize: 13,
              lineHeight: 1.45,
              cursor: readOnly ? 'default' : 'pointer',
              opacity: readOnly ? 0.85 : 1,
            }}
          >
            <input
              type="checkbox"
              checked={flags[c.key] === true}
              disabled={readOnly || saving}
              onChange={(e) => onToggle(c.key, e.target.checked)}
              style={{ marginTop: 2, flex: '0 0 auto' }}
            />
            <span>
              {c.label}{' '}
              <span style={{ color: 'var(--ink-mute)', fontSize: 11 }}>
                {c.required ? '(required)' : '(optional — only if applicable)'}
              </span>
            </span>
          </label>
        ))}

        {!readOnly && !hideAccept && (
          <div
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              marginTop: 4,
              flexWrap: 'wrap',
            }}
          >
            <Btn3 intent="grn" onClick={onAccept} disabled={!requiredOk || saving}>
              {saving
                ? 'Saving…'
                : alreadyAccepted
                ? 'Update & continue'
                : 'I agree — continue'}
            </Btn3>
            {!requiredOk && (
              <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                Please tick all required boxes to continue.
              </span>
            )}
          </div>
        )}
        {readOnly && alreadyAccepted && (
          <div style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
            You accepted this earlier. Use “Edit” above to change your choices.
          </div>
        )}
      </Box>
    </>
  );
}
